import { useEffect, useState } from 'react';
import { OptimusProvider, UnsavedChangesDialog } from '@mews-commander/ui';
import { FloatingPanel } from '@mews/b2b-ui';
import { createZodFormResolver, lt, useFormControls } from '@mews/framework';
import { Button, DialogProps, Stack } from '@optimus-web/core';
import { FormErrors } from '../../../enterpriseRateManagement/types';
import { useGetCentralProductDetail } from '../../queries/CentralProductsTable';
import { GuestFacingFields } from './fields/GuestFacingFields';
import { InternalFields } from './fields/InternalFields';
import { MappingFields } from './fields/MappingFields';
import { emptyDefaultValues, formSchema, type FormValues, type EditFormValues, FieldNames } from './formEnums';

type CentralProductDialogFormProps = Pick<DialogProps, 'active'> & {
    formErrors?: FormErrors<FormValues>;
    isLoading?: boolean;
    onCancel?: () => void;
    onSubmit: (formValues: FormValues | EditFormValues) => Promise<void>;
    centralProductId?: string;
};

export const CentralProductForm = ({
    active,
    formErrors,
    isLoading,
    onCancel,
    onSubmit,
    centralProductId,
}: CentralProductDialogFormProps) => {
    const { data, isFetching } = useGetCentralProductDetail(centralProductId);

    const isEditForm = !!centralProductId;
    const resolver = createZodFormResolver(isEditForm ? formSchema.omit({ [FieldNames.Key]: true }) : formSchema);

    const handleFormSubmit = async (values: FormValues) => {
        const { key, ...filteredValues } = values;
        await onSubmit(isEditForm ? filteredValues : values);
    };

    const { hasDirtyFields, renderFormContent, reset, setError, submit } = useFormControls<FormValues>({
        defaultValues: emptyDefaultValues,
        values: data,
        onSubmit: handleFormSubmit,
        resolver,
    });

    const [unsaved, setUnsaved] = useState(false);

    const handleClose = () => {
        if (hasDirtyFields) {
            setUnsaved(true);
            return;
        }

        handleDiscard();
    };

    const handleDiscard = () => {
        setUnsaved(false);
        onCancel?.();
    };

    useEffect(() => {
        if (formErrors?.field) {
            setError(formErrors.field, {
                message: formErrors.message,
            });
        }
    }, [formErrors, setError]);

    useEffect(() => {
        if (!active) {
            reset();
        }
    }, [active, reset]);

    return (
        <>
            <FloatingPanel
                footer={{
                    primary: (
                        <Button size="medium" variant="primary" onClick={submit} type="submit">
                            {isEditForm ? lt('Save') : lt('Create')}
                        </Button>
                    ),
                    secondary: (
                        <Button size="medium" variant="tertiary" onClick={handleClose} type="submit">
                            {lt('Cancel')}
                        </Button>
                    ),
                }}
                active={active}
                loading={isLoading || isFetching}
                onClose={handleClose}
                title={isEditForm ? lt('EditProduct') : lt('CreateProduct')}
            >
                <OptimusProvider density="spacious">
                    {renderFormContent(
                        <Stack vertical spacing="300">
                            <InternalFields />
                            <GuestFacingFields imageId={data?.imageId || undefined} />
                            <MappingFields isEdit={isEditForm} />
                        </Stack>
                    )}
                </OptimusProvider>
            </FloatingPanel>
            {unsaved ? (
                <UnsavedChangesDialog onCancel={() => setUnsaved(false)} onDiscardChanges={handleDiscard} />
            ) : null}
        </>
    );
};
